import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, colors } from '@insurancemenu/imenu-components-lib';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { useRootStore } from 'store/rootStore';
import { EnrollmentCardType, EnrollmentPeriod, EnrollmentStage, EnrollmentType } from 'types/enrollmentService';
import { checkIfCuttingOff } from 'global/utils/checkIfCuttingOff';
import { toJS } from 'mobx';
import { useHistory } from 'react-router-dom';
import { uwRights, employeeRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import EnrollmentTag from 'components/common/enrollment/EnrollmentTag';
import StageButton from 'components/common/enrollment/StageButton';
import clsx from 'clsx';
import { DriveFile, Edit, Menu } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Feature } from 'types/domainServiceV2';
import { createStyles } from './styles';

interface Props {
  enrollmentPeriod: EnrollmentPeriod;
  footer: React.ReactNode;
  onContinue?: () => void;
  type?: EnrollmentCardType;
}

const CommonEnrollmentCard: React.FC<Props> = observer(({ enrollmentPeriod, footer, onContinue, type = 'common' }) => {
  const { domainStoreV2, userStore, subscriberStore, enrollmentCardStore } = useRootStore();
  const convertedUserRoles = userStore.userRoles.map((role) => toJS(role)).slice();

  const history = useHistory();

  const showChangeEffectiveDateButton =
    uwRights(convertedUserRoles) && type === 'common' && enrollmentPeriod.stage === EnrollmentStage.ENROLLING;

  function redirect(url: string): void {
    history.push(url);
  }

  function onContinueClick(): void {
    if (onContinue) {
      onContinue();
    } else if (
      enrollmentPeriod.stage === EnrollmentStage.SUBMITTED &&
      domainStoreV2.domainHeader.properties.availableFeatures?.includes(Feature.POST_SUBMIT_CHECKLIST)
    ) {
      redirect(`/post-submit-checklist/${enrollmentPeriod.id}`);
    } else {
      redirect(`/checklist/${enrollmentPeriod.id}`);
    }
  }

  function onProposalRedirect(): void {
    const cartId = enrollmentPeriod.proposals.find((proposal) => proposal.confirmed)?.cartId;
    if (cartId) redirect(`/proposal-v2/${enrollmentPeriod.id}?groupId=${enrollmentPeriod.groupId}&cartId=${cartId}`);
  }

  function onPostSubmitChecklistRedirect(): void {
    redirect(`/post-submit-checklist/${enrollmentPeriod.id}`);
  }

  function getCuttingOffText({ cuttingOffInFortnight, cutOffInDays }: ReturnType<typeof checkIfCuttingOff>): string {
    if (cuttingOffInFortnight) {
      switch (cutOffInDays) {
        case 0:
          return 'Cut off';
        case 1:
          return 'Cuts off today';
        default:
          return `Cuts off in ${cutOffInDays} days`;
      }
    }
    return '';
  }

  const cuttingOffCheckResult = checkIfCuttingOff(
    enrollmentPeriod.effectiveDate,
    domainStoreV2.domainHeader.properties.retroActiveDaysAmount || 10,
  );

  // Hide effective date for renewal and installed cards in the previous policy section
  const hideEffectiveDate =
    (enrollmentPeriod.type === EnrollmentType.RENEWAL || enrollmentPeriod.type === EnrollmentType.INSTALLED) &&
    type === 'archived';

  const classes = createStyles();
  return (
    <div className={classes.enrollmentCard}>
      <header className={clsx(classes.cardHeader, hideEffectiveDate && classes.cardHeaderWithoutDate)}>
        <div className={classes.leftHeaderSection}>
          {!hideEffectiveDate && (
            <div>
              <div className={classes.effectiveDateWrapper}>
                <h6 className={classes.effectiveDateTitle}>Effective Date</h6>
                {showChangeEffectiveDateButton && (
                  <Button
                    color="custom"
                    customColor={colors.purple[600]}
                    type="icon"
                    onClick={(): void => {
                      enrollmentCardStore.updateChangeEffectiveDateModalStatus(true, {
                        periodId: enrollmentPeriod.id,
                        effectiveDate: enrollmentPeriod.effectiveDate,
                      });
                    }}
                  >
                    <Edit />
                  </Button>
                )}
              </div>
              <h5 className={classes.effectiveDateValue}>
                {dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}
              </h5>
            </div>
          )}
          <div className={classes.stageTagContainer}>
            <EnrollmentTag
              enrollmentPeriod={enrollmentPeriod}
              isSubscriberElected={subscriberStore.isSubscriberElected}
              userRoles={convertedUserRoles}
            />
          </div>
        </div>
        <div className={classes.rightHeaderSection}>
          {type === 'common' && (
            <>
              <Button color="primary" size="small" width="18.4rem" onClick={onContinueClick}>
                Continue
              </Button>
              {(cuttingOffCheckResult.cuttingOffInFortnight || cuttingOffCheckResult.cuttingOffInFortnight) && (
                <p className={classes.expirationReminder}>{getCuttingOffText(cuttingOffCheckResult)}</p>
              )}
            </>
          )}
          {type === 'archived' && enrollmentPeriod.hasConfirmedProposal && (
            <>
              {enrollmentPeriod.stage === EnrollmentStage.SOLD ? (
                <div className={classes.soldButtonsWrapper}>
                  <Button
                    color="primary"
                    size="small"
                    type="text"
                    leftIconProp={<DriveFile />}
                    className={classes.actionButton}
                    onClick={onProposalRedirect}
                  >
                    View Proposal
                  </Button>
                  {domainStoreV2.domainHeader.properties.availableFeatures?.includes(Feature.POST_SUBMIT_CHECKLIST) && (
                    <Button
                      color="primary"
                      size="small"
                      type="text"
                      className={classes.actionButton}
                      onClick={onPostSubmitChecklistRedirect}
                      leftIconProp={<Menu />}
                    >
                      Post Submit Checklist
                    </Button>
                  )}
                </div>
              ) : (
                <Button color="primary" size="small" width="18.4rem" onClick={onContinueClick}>
                  View
                </Button>
              )}
            </>
          )}
          {type === 'ee' && (
            <Button color="primary" size="small" width="18.4rem" onClick={onContinueClick}>
              Continue
            </Button>
          )}
        </div>
      </header>

      <footer className={classes.footer}>
        {footer}
        {!employeeRights(convertedUserRoles) && <StageButton enrollmentPeriod={enrollmentPeriod} />}
      </footer>
    </div>
  );
});

export default CommonEnrollmentCard;
